import { useEffect, useState, useRef } from "react";
import MailCard from "../../../components/email/mail/CenterCard";
import MailHistory from "../../../components/email/mail/MailHistory";
import ReplyCard from "../../../components/email/mail/ReplyCard";
import { Link, useParams } from "react-router-dom";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  useMailHistoryQuery,
  useSpecificThreadsQuery,
  useUpdateDispositionMutation,
  useUpdateNonActionableMutation,
  useUpdateActionableMutation,
  useEmailAuditTrailQuery,
} from "../../../Services/email/EmailInbox";
import ReactModal from "react-modal";
import ReplyDispositionModal from "../../../components/ticket/ReplyDispositionModal";
import "../../contact/styles.scss";
import EmailTicketModal from "../../../components/email/inboxEmailModal/EmailTicketModal";
import SLMetricsTimer from "./SLMetricsTimer";
import TicketLeftSideMenuCard from "../../../components/ticket/TicketLeftSideMenuCard";
import ChangeUserModal from "../../../components/ticket/ticketListInfo/ChangeUserModal";
import ForwardCard from "./ForwardCard";
import ShortcutIcon from "@mui/icons-material/Shortcut";
import { Pagination, PaginationItem, Tooltip } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GroupIcon from "@mui/icons-material/Group";
import EmailSLMetricsModal from "../../../components/email/mail/EmailSLMetricsModal";
import KnowledgeBaseModal from "../../../components/ticket/KnowledgeBaseModal";
import OurTeamModal from "../../../components/ticket/OurTeamModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/reusableComponent/NonActionableConfirmationModal";
import EmailAuditTrail from "./EmailAuditTrail";
import "./Mail.scss";

interface IProps {
  signatureData: any;
}

const Mail: React.FC<IProps> = ({ signatureData }) => {
  const [showReplyCard, setShowReplyCard] = useState(false);
  const [showForwardCard, setShowForwardCard] = useState(false);
  const [mailDetails, setMailDetails] = useState([]);
  const [otherMailDetails, setOtherMailDetails] = useState<any>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dispositionModal, setDispositionModal] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<any>("");
  const [mailHistoryContactId, setMailHistoryContactId] = useState<any>("");
  const params = useParams();
  const [refetch, setRefetch] = useState(false);
  const [storedDispositionData, setStoredDispositionData] = useState([]);
  const [storedTicketId, setStoredTicketId] = useState("");
  const [isModalOpenTicket, setIsModalOpenTicket] = useState(false);
  const replyCardRef = useRef<HTMLDivElement | null>(null);
  const printRef = useRef<HTMLDivElement | null>(null);
  const forwardCardRef = useRef<HTMLDivElement | null>(null);
  const [isSLMetricsExpanded, setIsSLMetricsExpanded] = useState(false);
  const [mailHistoryPage, setMailHistoryPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [channelHistoryLoading, setChannelHistoryLoading] = useState(false);
  const [isSLMetricsModalOpen, setIsSLMetricsModalOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] =
    useState(false);
  const [isOurTeamModalOpen, setIsOurTeamModalOpen] = useState(false);

  // ── Tab state (mirrors TicketPaneOpen paneOpen) ──────────────────────────
  const [activeTab, setActiveTab] = useState<"history" | "auditTrail">(
    "history",
  );

  // Local optimistic state for is_non_actionable
  const [isNonActionable, setIsNonActionable] = useState<boolean>(false);

  // Confirmation modal for mark actionable / non-actionable
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const uuid = params.uuid;

  const {
    data: specificThreads,
    isLoading,
    refetch: refetchSpecificThreads,
  } = useSpecificThreadsQuery(uuid);

  // ── History — only fires when history tab is active ──────────────────────
  const { data: mailHistory, status: mailHistoryStatus } = useMailHistoryQuery(
    { contact: mailHistoryContactId, page: mailHistoryPage },
    { skip: !mailHistoryContactId || activeTab !== "history" },
  );

  // ── Email audit trail — only fires when audit trail tab is active ─────────
  const { data: emailAuditTrailData = [], isLoading: isAuditTrailLoading  , refetch: refetchEmailTrail} =
    useEmailAuditTrailQuery(
      { id: otherMailDetails?.id },
      { skip: !otherMailDetails?.id || activeTab !== "auditTrail" },
    );

  const [trigger, { isLoading: loading }] = useUpdateDispositionMutation();
  const [triggerNonActionable, { isLoading: isNonActionableLoading }] =
    useUpdateNonActionableMutation();
  const [triggerActionable, { isLoading: isActionableLoading }] =
    useUpdateActionableMutation();

  const isActionableToggleLoading =
    isNonActionableLoading || isActionableLoading;

  useEffect(() => {
    setStoredDispositionData(
      otherMailDetails?.action?.disposition?.disposition,
    );
  }, [otherMailDetails?.action?.disposition?.disposition]);

  useEffect(() => {
    if (specificThreads) {
      setOtherMailDetails(specificThreads);
      setMailHistoryContactId(specificThreads?.action?.contact?.id);
      setStoredTicketId(specificThreads?.action?.ticket?.id);
      setIsNonActionable(!!specificThreads?.is_non_actionable);
    }
  }, [specificThreads]);

  useEffect(() => {
    if (mailHistory) setTotalPages(Math.ceil((mailHistory?.count || 0) / 5));
  }, [mailHistory]);

  useEffect(() => {
    if (specificThreads?.emails) setMailDetails(specificThreads?.emails);
  }, [specificThreads]);

  useEffect(() => {
    if (refetch) {
      refetchSpecificThreads();
      setRefetch(false);
    }
  }, [refetch, refetchSpecificThreads]);

  useEffect(() => {
    if (mailHistoryStatus === "pending") setChannelHistoryLoading(true);
    else if (mailHistoryStatus === "fulfilled") setChannelHistoryLoading(false);
  }, [mailHistoryStatus]);

  const handleNonActionableToggle = async () => {
    const threadId = otherMailDetails?.id;
    if (!threadId) return;

    if (isNonActionable) {
      try {
        const response = await triggerActionable(threadId);

        if (response?.data) {
          setIsNonActionable(false);
          setOtherMailDetails((prev: any) => ({
            ...prev,
            is_non_actionable: false,
            email_first_response_sla_info:
              specificThreads?.email_first_response_sla_info ?? [],
          }));
          toast.success("Email marked as actionable");
          refetchSpecificThreads();
        } else if (response?.error) {
          toast.error("Unable to update non-actionable status");
        }
      } catch {
        toast.error("An error occurred while updating non-actionable status");
      }
      return;
    }

    const newValue = true;

    setIsNonActionable(newValue);
    setOtherMailDetails((prev: any) => ({
      ...prev,
      is_non_actionable: newValue,
      email_first_response_sla_info: [],
    }));

    try {
      const response = await triggerNonActionable({
        body: { is_non_actionable: newValue },
        id: threadId,
      });

      if (response?.data) {
        toast.success("Email marked as non-actionable");
        refetchSpecificThreads();
      } else if (response?.error) {
        setIsNonActionable(false);
        setOtherMailDetails((prev: any) => ({
          ...prev,
          is_non_actionable: false,
          email_first_response_sla_info:
            specificThreads?.email_first_response_sla_info ?? [],
        }));
        toast.error("Unable to update non-actionable status");
      }
    } catch {
      setIsNonActionable(false);
      setOtherMailDetails((prev: any) => ({
        ...prev,
        is_non_actionable: false,
        email_first_response_sla_info:
          specificThreads?.email_first_response_sla_info ?? [],
      }));
      toast.error("An error occurred while updating non-actionable status");
    }
  };

  const toggleReplyCard = (text: string) => {
    setReplyText(text);
    setShowForwardCard(false);
    setShowReplyCard((prev) => {
      const shouldShow = !prev;
      if (text === "replyAll" || (text === "reply" && shouldShow)) {
        setTimeout(
          () => replyCardRef.current?.scrollIntoView({ behavior: "smooth" }),
          0,
        );
      }
      return shouldShow;
    });
  };

  const toggleForwardCard = () => {
    setShowReplyCard(false);
    setShowForwardCard((prev) => {
      const shouldShow = !prev;
      if (shouldShow) {
        setTimeout(
          () => forwardCardRef.current?.scrollIntoView({ behavior: "smooth" }),
          0,
        );
      }
      return shouldShow;
    });
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      const threadInfo = specificThreads || otherMailDetails;
      const emailsData = threadInfo?.emails || mailDetails || [];
      const currentDate = new Date();
      const formattedDate = `${currentDate
        .getDate()
        .toString()
        .padStart(2, "0")}/${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${currentDate.getFullYear()}, ${currentDate
        .getHours()
        .toString()
        .padStart(2, "0")}:${currentDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;

      const printHTML = `<!DOCTYPE html><html><head><title>Print Email</title></head><body>
        <p>${formattedDate}</p>${formatEmailsForPrint(
        emailsData,
      )}</body></html>`;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
    }
  };

  const formatEmailsForPrint = (emails: any[]) => {
    if (!emails || emails.length === 0) return "<p>No emails to display</p>";
    return emails
      .map((email) => {
        const emailBody =
          email.html_content || email.body || "No content available";
        return `<div style="border:1px solid #ddd;margin-bottom:20px;padding:20px;">
        <p><strong>From:</strong> ${email.receive_from || "Unknown"}</p>
        <p><strong>Date:</strong> ${
          email.mail_sent_or_received_on
            ? new Date(email.mail_sent_or_received_on).toLocaleString()
            : "Unknown"
        }</p>
        <div>${emailBody}</div>
      </div>`;
      })
      .join("");
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((part) => part[0])
      .join("");

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "450px",
    },
  };

  const isMobile = window.innerWidth < 600;

  const customModalStyles2: any = {
    content: {
      top: isMobile ? "0" : "50%",
      left: isMobile ? "0" : "50%",
      right: "auto",
      bottom: "auto",
      transform: isMobile ? "none" : "translate(-50%, -50%)",
      width: isMobile ? "100%" : "100%",
      maxWidth: isMobile ? "100%" : "550px",
      height: "auto",
      maxHeight: isMobile ? "600px" : "700px",
      overflowY: "auto",
      padding: "20px",
      borderRadius: isMobile ? "0" : "12px",
      display: "block",
    },
  };

  const customModalStylesTicket = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "100%",
      maxWidth: "850px",
      height: "80%",
      margin: "auto",
    },
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openModalTicket = () => setIsModalOpenTicket(true);
  const closeModalTicket = () => setIsModalOpenTicket(false);
  const openDispositionModal = () => setDispositionModal(true);
  const closeDispositionModal = () => setDispositionModal(false);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setMailHistoryPage(page);
  };

  const assigneeName = otherMailDetails?.action?.assignee
    ? `${otherMailDetails?.action?.assignee?.username} `
    : "Unassigned";

  const slaMetricsData: any = {
    email_first_response_sla_info:
      specificThreads?.email_first_response_sla_info || [],
    is_replied: otherMailDetails?.is_replied,
    emails: otherMailDetails?.emails || [],
  };

  const tabs = [
    { id: "history", label: "History" },
    { id: "auditTrail", label: "Email Audit Trail" },
  ];

  const renderNonActionableBtn = () => {
    if (isNonActionable) {
      return (
        <Tooltip title="Mark as actionable" arrow placement="top">
          <button
            className="btn btn-active-light-primary btn-icon"
            aria-label="Mark as actionable"
            onClick={() => setConfirmModalOpen(true)}
            disabled={isActionableToggleLoading}
            style={{
              opacity: isActionableToggleLoading ? 0.6 : 1,
              cursor: isActionableToggleLoading ? "wait" : "pointer",
            }}
          >
            <i
              className="bi bi-envelope-check"
                style={{ fontSize: "18px", color: "#94a3b8" }}
            />
          </button>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Mark as non-actionable" arrow placement="top">
        <button
          className="btn btn-active-light-primary btn-icon"
          aria-label="Mark as non-actionable"
          onClick={() => setConfirmModalOpen(true)}
          disabled={isActionableToggleLoading}
          style={{
            opacity: isActionableToggleLoading ? 0.6 : 1,
            cursor: isActionableToggleLoading ? "wait" : "pointer",
          }}
        >
          <i
            className="bi bi-envelope-slash"
            style={{ fontSize: "18px", color: "#6B7280" }}
          />
        </button>
      </Tooltip>
    );
  };

  return (
    <>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <div className="mail-list-info-container">
          <div className="mail-info-wrapper">
            <div className="mail-info-sticky-header">
            <div className="mail-info-header">
              <div className="header-left">
                <h2 className="page-title">Mail information</h2>
              </div>
              <div className="header-right">
                <button
                  className="action-button sl-metrics-btn"
                  onClick={() => setIsSLMetricsModalOpen(true)}
                  disabled={
                    !specificThreads?.email_first_response_sla_info?.length
                  }
                >
                  <AssessmentIcon fontSize="small" />
                  <span>SL Matrix</span>
                </button>
                {/* <button
                  className="action-button knowledge-base-btn"
                  onClick={() => setIsKnowledgeBaseModalOpen(true)}
                  disabled
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <MenuBookIcon fontSize="small" />
                  <span>Knowledge base</span>
                </button> */}
                {/* <button
                  className="action-button our-team-btn"
                  onClick={() => setIsOurTeamModalOpen(true)}
                  disabled
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <GroupIcon fontSize="small" />
                  <span>Our team</span>
                </button> */}
              </div>
            </div>
            </div>

            {mailDetails?.length === 0 ? (
              <div className="no-data">No data available.</div>
            ) : (
              <div className="mail-content-layout">
                <div className="left-sidebar">
                  <TicketLeftSideMenuCard tickets={otherMailDetails} />
                </div>

                <div className="main-contentttt mail-main-scroll">
                  <div className="flex-lg-row-fluid">
                    {/* ── Email Card ── */}
                    <div className="email-cardddd">
                      <div className="card-header align-items-center py-5 gap-5">
                        <div className="d-flex justify-content-between w-100">
                          <div className="d-flex">
                            <button
                              className="btn btn-active-light-primary btn-icon"
                              title="Reply"
                              onClick={() => toggleReplyCard("reply")}
                            >
                              <i className="bi bi-reply fs-2"></i>
                            </button>
                            <button
                              className="btn btn-active-light-primary btn-icon"
                              title="Reply all"
                              onClick={() => toggleReplyCard("replyAll")}
                            >
                              <i className="bi bi-reply-all-fill fs-2"></i>
                            </button>
                            <button
                              className="btn btn-active-light-primary btn-icon"
                              title="Forward"
                              onClick={toggleForwardCard}
                            >
                              <ShortcutIcon
                                className="text-gray-500"
                                style={{ fontSize: "20px" }}
                              />
                            </button>

                            {otherMailDetails?.action?.ticket?.id ? (
                              <button
                                className="btn btn-active-light-primary btn-icon"
                                title="Process Ticket"
                              >
                                <Link
                                  to={`/ticket/information/${otherMailDetails?.action?.ticket?.id}`}
                                >
                                  <i className="bi bi-tag fs-2"></i>
                                </Link>
                              </button>
                            ) : (
                              <button
                                className="btn btn-active-light-primary btn-icon"
                                title="Create Ticket"
                                onClick={openModalTicket}
                              >
                                <i className="bi bi-tag fs-2"></i>
                              </button>
                            )}

                            <button
                              className="btn btn-active-light-primary btn-icon"
                              title="View Disposition"
                              onClick={openDispositionModal}
                            >
                              <i
                                className="bi bi-eye-fill"
                                style={{ fontSize: "18px" }}
                              ></i>
                            </button>

                            <button
                              className="btn btn-active-light-primary btn-icon"
                              title="Print Email"
                              onClick={handlePrint}
                            >
                              <i
                                className="bi bi-printer-fill"
                                style={{ fontSize: "18px" }}
                              ></i>
                            </button>

                            {renderNonActionableBtn()}
                          </div>

                          <div
                            className="d-flex cursor-pointer"
                            onClick={openModal}
                          >
                            <div className="symbol symbol-circle">
                              <span className="symbol-label bg-light-danger text-danger fw-bolder">
                                {getInitials(assigneeName)}
                              </span>
                            </div>
                            <span
                              className="assign-name mt-4"
                              style={{ marginLeft: "5px" }}
                            >
                              {assigneeName}
                              <i
                                className="fas fa-chevron-down"
                                style={{ marginLeft: "5px" }}
                              ></i>
                            </span>
                          </div>
                        </div>

                        {otherMailDetails?.email_first_response_sla_info
                          ?.length > 0 && (
                          <SLMetricsTimer
                            firstResponseSlaBreach={
                              otherMailDetails?.email_first_response_sla_info
                            }
                            statusType={
                              otherMailDetails?.action?.ticket?.status_type
                            }
                            otherMailDetails={otherMailDetails}
                            isPaused={isNonActionable}
                          />
                        )}
                      </div>

                      <div className="card p-4" ref={printRef}>
                        <MailCard
                          otherMailDetails={otherMailDetails}
                          mailDetails={mailDetails}
                        />
                      </div>
                    </div>

                    {/* ── Reply Card ── */}
                    <div
                      className="p-1"
                      style={{ margin: "0" }}
                      ref={replyCardRef}
                    >
                      {showReplyCard && (
                        <ReplyCard
                          otherMailDetails={otherMailDetails}
                          mailDetails={mailDetails}
                          replyText={replyText}
                          uuid={uuid}
                          refetchSpecificThreads={refetchSpecificThreads}
                          setShowReplyCard={setShowReplyCard}
                          storedMailHistory={storedDispositionData}
                          storedTicketId={storedTicketId}
                          setReplyText={setReplyText}
                          signatureData={signatureData}
                        />
                      )}
                    </div>

                    {/* ── Forward Card ── */}
                    <div
                      className="p-1"
                      style={{ margin: "0" }}
                      ref={forwardCardRef}
                    >
                      {showForwardCard && (
                        <ForwardCard
                          otherMailDetails={otherMailDetails}
                          mailDetails={mailDetails}
                          uuid={uuid}
                          refetchSpecificThreads={refetchSpecificThreads}
                          refetchEmailTrail={refetchEmailTrail}
                          setShowForwardCard={setShowForwardCard}
                          signatureData={signatureData}
                        />
                      )}
                    </div>

                    <div className="mt-5">
                      {/* Tab navigation — identical structure to TicketPaneOpen */}
                      <div className="ticket-tabs-container">
                        <div className="ticket-tabs">
                          {tabs.map((tab) => (
                            <button
                              key={tab.id}
                              className={`ticket-tab ${
                                activeTab === tab.id ? "active" : ""
                              }`}
                              onClick={() =>
                                setActiveTab(tab.id as "history" | "auditTrail")
                              }
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tab content — identical structure to TicketPaneOpen */}
                      <div className="ticket-content-card">
                        {activeTab === "history" && (
                          <>
                            {channelHistoryLoading ? (
                              <div className="text-center py-3">
                                <LoadingSpinner />
                              </div>
                            ) : (
                              <>
                                <MailHistory
                                  mailHistory={mailHistory?.results || []}
                                />
                                <div
                                  className="d-flex flex-row-reverse mt-4 mb-4"
                                  style={{ marginRight: "20px" }}
                                >
                                  <Pagination
                                    sx={{
                                      display: "flex",
                                      justifyContent: "end",
                                      "& .MuiPaginationItem-root": {
                                        fontSize: "14px",
                                        minWidth: "36px",
                                        height: "36px",
                                        margin: "0 2px",
                                        borderRadius: "6px",
                                        border: "1px solid #e5e7eb",
                                        color: "#374151",
                                        "&:hover": {
                                          backgroundColor: "#f9fafb",
                                          borderColor: "#d1d5db",
                                        },
                                        "&.Mui-selected": {
                                          backgroundColor: "#6366f1",
                                          color: "#ffffff",
                                          borderColor: "#6366f1",
                                          "&:hover": {
                                            backgroundColor: "#4f46e5",
                                            borderColor: "#4f46e5",
                                          },
                                        },
                                      },
                                    }}
                                    count={totalPages}
                                    page={mailHistoryPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    renderItem={(item) => {
                                      if (item.type === "page") {
                                        const isNearCurrent =
                                          Math.abs(
                                            item.page - mailHistoryPage,
                                          ) <= 1 ||
                                          item.page === 1 ||
                                          item.page === totalPages;
                                        return isNearCurrent ? (
                                          <PaginationItem {...item} />
                                        ) : null;
                                      }
                                      return <PaginationItem {...item} />;
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </>
                        )}

                        {activeTab === "auditTrail" && (
                          <>
                            {isAuditTrailLoading ? (
                              <div className="text-center py-3">
                                <LoadingSpinner />
                              </div>
                            ) : (
                              <EmailAuditTrail
                                emailAuditTrailData={emailAuditTrailData}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={customModalStyles}
        contentLabel="Email templates"
        shouldCloseOnOverlayClick={false}
      >
        <ChangeUserModal
          closeModal={closeModal}
          refetch={refetchSpecificThreads}
          refetchAuditTrailForwardedData={refetchSpecificThreads}
          refetchEmailTrail={refetchEmailTrail}
          userChangeType="Email"
        />
      </ReactModal>

      <ReactModal
        isOpen={dispositionModal}
        onRequestClose={closeDispositionModal}
        style={customModalStyles2}
        contentLabel="Disposition"
        shouldCloseOnOverlayClick={false}
      >
        <ReplyDispositionModal
          isLoading={loading}
          trigger={trigger}
          closeModal={closeDispositionModal}
          storedDisposition={storedDispositionData}
          refetch={refetchSpecificThreads}
          refetchEmailTrail={refetchEmailTrail}
          ticketId={storedTicketId}
          otherMailDetails={otherMailDetails}
          value={""}
        />
      </ReactModal>

      <ReactModal
        isOpen={isModalOpenTicket}
        onRequestClose={closeModalTicket}
        style={customModalStylesTicket}
        contentLabel="New Ticket"
        shouldCloseOnOverlayClick={false}
      >
        <EmailTicketModal
          closeModal={closeModalTicket}
          refetch={refetchSpecificThreads}
          refetchEmailTrail={refetchEmailTrail}
          specificThreads={specificThreads}
        />
      </ReactModal>

      <EmailSLMetricsModal
        isOpen={isSLMetricsModalOpen}
        onClose={() => setIsSLMetricsModalOpen(false)}
        emailData={slaMetricsData}
      />
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
      />
      <OurTeamModal
        isOpen={isOurTeamModalOpen}
        onClose={() => setIsOurTeamModalOpen(false)}
      />

      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleNonActionableToggle}
        title="Confirm Action"
        message={
          isNonActionable ? (
            <>
              Are you sure you want to{" "}
              <strong>mark this email as actionable</strong>?
            </>
          ) : (
            <>
              Are you sure you want to{" "}
              <strong>mark this email as non-actionable</strong>?
            </>
          )
        }
        confirmLabel={"Confirm"}
        confirmButtonClass={"btn btn-primary"}
      />
    </>
  );
};

export default Mail;
